import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useCallback, useEffect, useId, useState } from "react";
import { useMediaQuery } from "../../hooks";
import { Drawer } from "../Drawer";
import { ModalContent } from "./ModalContent";
import { ModalContext } from "./context";
import type { ModalProps } from "./types";

export const Modal = (props: ModalProps) => {
  const { open, onOpenChange } = props;
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);
  const isMobile = useMediaQuery("(max-width: 639px)");
  const titleId = useId();
  const descriptionId = useId();

  const { context: floatingContext } = useFloating({
    open,
    onOpenChange,
  });

  const dismiss = useDismiss(floatingContext, {
    enabled: !props.preventClose && !isMobile,
    escapeKey: !props.preventClose,
    outsidePress: props.preventClose ? false : props.closeOnOutsidePress,
  });

  const { getFloatingProps } = useInteractions([dismiss]);

  const handleDismiss = useCallback((): void => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAnimationEnd = useCallback((): void => {
    if (!open) {
      setMounted(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const rafId = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(rafId);
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!mounted && !open) {
    return null;
  }

  const contextValue = {
    ...props,
    handleDismiss,
    floatingContext,
    getFloatingProps,
    handleAnimationEnd,
    mounted,
    visible,
    titleId,
    descriptionId,
  };

  if (isMobile) {
    return (
      <ModalContext.Provider value={contextValue}>
        <Drawer {...props} side="bottom" />
      </ModalContext.Provider>
    );
  }

  return (
    <ModalContext.Provider value={contextValue}>
      <FloatingPortal>
        {mounted && (
          <FloatingOverlay
            className="bg-black/50 z-[60] flex items-center justify-center p-4"
            lockScroll
          >
            <FloatingFocusManager
              context={floatingContext}
              returnFocus={true}
              initialFocus={0}
              modal={true}
            >
              <ModalContent />
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    </ModalContext.Provider>
  );
};
